/**
 * Sticker rendering utilities — plan 120, PR5.
 *
 * Pure helpers shared by the admin and visitor sticker overlays. Lives in
 * `packages/maps` (no Deck.gl import) so both consumers compute identical
 * opacity/zoom behavior; closes the PR4 smoke gap where admin ignored
 * `min_zoom` / `max_zoom` and showed stickers at every zoom.
 *
 * v2 seam: `resolveZoomRange(inst, theme)` is the single source of truth for
 * which zoom window to fade against. Today it returns the per-instance
 * values verbatim. Plan 120 v2 swaps the implementation to fall back to
 * `theme.stickers.defaultMinZoom` / `defaultMaxZoom` when the row's columns
 * are NULL — the call sites here don't change.
 */
import type { ThemeConfig } from "../themes/types";

/** Width of the opacity ramp past min_zoom / max_zoom edges, in zoom units. */
export const STICKER_ZOOM_FADE_RANGE = 0.5;

export interface StickerZoomInput {
  /** Per-instance override; NULL → fall back to theme.stickers.defaultMinZoom. */
  minZoom: number | null;
  maxZoom: number | null;
}

/** Hard-coded floor used when neither per-instance nor theme provides a value. */
const STICKER_DEFAULT_MIN_ZOOM = 14;
const STICKER_DEFAULT_MAX_ZOOM = 22;

/**
 * Compute opacity multiplier for a sticker at the given zoom. Returns 1 when
 * fully inside [minZ, maxZ], 0 outside the ±0.5 ramp, and a linear ramp at
 * the edges. Narrow window (maxZ - minZ < 1) → ramps overlap; `min(rampIn,
 * rampOut)` keeps the result in [0, 1] without going negative or > 1.
 */
export function zoomFactor(
  z: number,
  minZ: number,
  maxZ: number,
  fadeRange: number = STICKER_ZOOM_FADE_RANGE,
): number {
  if (z < minZ - fadeRange || z > maxZ + fadeRange) return 0;
  const rampIn = z < minZ ? (z - (minZ - fadeRange)) / fadeRange : 1;
  const rampOut = z > maxZ ? (maxZ + fadeRange - z) / fadeRange : 1;
  return Math.max(0, Math.min(rampIn, rampOut));
}

/**
 * Resolve the effective zoom range for a sticker instance. PR9 fallback
 * chain: per-instance value → `theme.stickers.defaultMinZoom`/`defaultMaxZoom`
 * → hard-coded `14` / `22`. Per-instance NULL means "inherit theme default"
 * — set by the override checkbox in `StickerEditPanel`; new stickers default
 * NULL so theme-driven look is the baseline.
 */
export function resolveStickerZoomRange(
  inst: StickerZoomInput,
  theme?: ThemeConfig,
): { minZoom: number; maxZoom: number } {
  const themeMin = theme?.stickers?.defaultMinZoom;
  const themeMax = theme?.stickers?.defaultMaxZoom;
  return {
    minZoom: inst.minZoom ?? themeMin ?? STICKER_DEFAULT_MIN_ZOOM,
    maxZoom: inst.maxZoom ?? themeMax ?? STICKER_DEFAULT_MAX_ZOOM,
  };
}

/**
 * Compute the [r, g, b, a] tuple for a sticker IconLayer's `getColor`,
 * combining base opacity with the zoom-fade factor. White RGB so the atlas
 * texture is rendered as authored; alpha drives the gating.
 */
export function stickerIconColor(
  baseOpacity: number,
  z: number,
  minZ: number,
  maxZ: number,
): [number, number, number, number] {
  const a = Math.round(255 * baseOpacity * zoomFactor(z, minZ, maxZ));
  return [255, 255, 255, a];
}

/**
 * Compute the shadow alpha tuple. Shadow icon mapping is masked, so the RGB
 * channels are ignored at render time — we still emit black for clarity.
 * The shadow ramps with the same `zoomFactor` so it never outlives the icon.
 */
export function stickerShadowColor(
  baseOpacity: number,
  shadowOpacity: number,
  z: number,
  minZ: number,
  maxZ: number,
): [number, number, number, number] {
  const a = Math.round(
    255 * baseOpacity * shadowOpacity * zoomFactor(z, minZ, maxZ),
  );
  return [0, 0, 0, a];
}

/**
 * Default sticker shadow tuning. Mirrors the prop shadow constants in
 * `deck-icon-mapping.ts`; tweaks live here so the sticker visual language can
 * diverge from props if a designer asks.
 */
export const STICKER_SHADOW_DEFAULT_OPACITY = 0.3;
/** Pixel offset at the reference zoom (z18). Scaled by `Math.pow(2, z-18)`. */
export const STICKER_SHADOW_DEFAULT_OFFSET_PX = 3;
export const STICKER_SHADOW_REFERENCE_ZOOM = 18;

/**
 * Pixel offset for the sticker shadow at a given zoom level. Mirrors the
 * prop shadow approach (full exponential scale) so a sticker placed next
 * to a prop reads as belonging to the same scene.
 */
export function stickerShadowOffsetPx(
  zoom: number,
  direction: { x: number; y: number } = { x: 1, y: 1 },
  basePx: number = STICKER_SHADOW_DEFAULT_OFFSET_PX,
): [number, number] {
  const scale = Math.pow(2, zoom - STICKER_SHADOW_REFERENCE_ZOOM);
  return [basePx * direction.x * scale, basePx * direction.y * scale];
}
